/**
 * Represents the possible status of a group member.
 *
 * - `active`: Member can participate in new expenses.
 * - `inactive`: Member is excluded from new expenses but kept for historical data.
 */
export const CMemberStatus = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
} as const;

/**
 * Union type of all possible member statuses.
 */
export type MemberStatus = (typeof CMemberStatus)[keyof typeof CMemberStatus];

/**
 * Represents a group of users sharing expenses.
 */
export interface IGroup {
  /** Unique identifier of the group */
  group_id: string;

  /** Display name of the group */
  name: string;

  /** Emoji associated with the group (for UI purposes) */
  emoji: string;

  /** Identifier of the user who owns the group */
  owner_id: string;
}

/**
 * Represents a suggested payment to settle debts between members.
 *
 * Example:
 * - from_id pays `amount` to to_id
 */
export interface IUserSettlementSuggestion {
  /** Debtor member ID */
  from_id: string;

  /** Creditor member ID */
  to_id: string;

  /** Amount to be paid */
  amount: number;
}

/**
 * Represents a member in the group dashboard context.
 */
export interface IDashboardMember {
  /** Unique identifier of the member within the split system */
  split_user_id: string;

  /** Group identifier */
  group_id: string;

  /** Display name shown in the UI */
  display_name: string;

  /** Current status of the member */
  status: MemberStatus;

  /** Optional alternative names or labels */
  aliases?: string[];

  /** Linked authenticated user ID (if exists) */
  modak_user_id?: string;

  /**
   * Net balance of the member:
   * - Positive: others owe this member
   * - Negative: this member owes others
   */
  balance?: number;
}

/**
 * Represents the net balance for a member.
 */
export interface INetByMember {
  /** Member identifier */
  member_id: string;

  /** Net amount (positive or negative) */
  net_amount: number;
}

/**
 * Response for: GET /groups/{group_id}
 *
 * Contains full group dashboard data including members and settlement suggestions.
 */
export interface IGetGroupDashboardResponse {
  /** Group metadata */
  group: IGroup;

  /** List of group members */
  members: IDashboardMember[];

  /** Suggested payments to settle balances */
  who_pays_whom: IUserSettlementSuggestion[];

  /** ISO timestamp of creation */
  created_at: string;

  /** ISO timestamp of last update */
  updated_at: string;
}

/**
 * Request for: POST /groups/:groupId/join/start
 *
 * Initiates the OTP flow to join a group.
 */
export interface IJoinGroupStartRequest {
  /** Group identifier (from path) */
  group_id: string;

  /** User phone number used for OTP */
  phone_number: string;

  /** Display name to be used in the group */
  display_name?: string;

  /**
   * Optional member ID.
   * If provided, links the phone number to an existing member.
   */
  split_user_id?: string;
}

/**
 * Response for: POST /groups/:groupId/join/start
 */
export interface IJoinGroupStartResponse {
  /** Generated or existing member ID */
  split_user_id: string;
}

/**
 * Request for: POST /groups/:groupId/join/verify
 *
 * Verifies the OTP and completes the join process.
 */
export interface IJoinGroupVerifyRequest {
  /** Group identifier (from path) */
  group_id: string;

  /** Member identifier */
  split_user_id: string;

  /** OTP code received by the user */
  code: string;
}

/**
 * Response for: POST /groups/:groupId/join/verify
 */
export interface IJoinGroupVerifyResponse {
  /** Confirmed member ID */
  split_user_id: string;
}

/**
 * Request for: POST /groups/:groupId/join/resend
 *
 * Resend the OTP to the user.
 */
export interface IJoinGroupResendRequest {
  /** Group identifier (from path) */
  group_id: string;

  /** Member identifier */
  split_user_id: string;
}

/**
 * Summary of a group for the user dashboard.
 */
export interface IUserGroupSummary {
  /** Group identifier */
  group_id: string;

  /** Group name */
  name: string;

  /** Emoji representation */
  emoji: string;

  /** Group description */
  description: string;

  /** Owner ID */
  owner_id: string;

  /** Owner display name */
  owner_name: string;

  /**
   * Simplified balance:
   * - Positive: user is owed money
   * - Negative: user owes money
   */
  balance_summary: number;
}

/**
 * Response for:
 * - GET /users/{split_user_id}/dashboard
 * - GET /users/dashboard (authenticated user)
 */
export interface IUserDashboardResponse {
  /** User identifier */
  user_id: string;

  /** List of groups the user belongs to */
  groups: IUserGroupSummary[];
}

/**
 * Extended expense object returned after recording a split.
 * Extends the base expense shape with additional fields present in the response.
 */
export interface IGroupExpenseSplit {
  id: string;
  group_id: string;
  created_at: string;
  owner_id: string;
  owner_split_user_id: string;
  session_id: string;
  payment_link: string;
  detail: IGroupExpenseDetail; // reuse existing
  participants: IGroupExpenseParticipant[]; // reuse existing
  payers: IRecordGroupExpenseParticipant[];
  is_complete: boolean;
  metadata: Record<string, unknown> | null;
  owner_role: string;
}

/**
 * Response for:
 * - POST /groups/{groupID}/split
 * - POST /groups/{groupID}/anonymous/split
 */
export interface IRecordGroupExpenseResponse {
  status: string; // e.g. "accepted"
  split: IGroupExpenseSplit;
}

/**
 * Request for recording a group expense.
 *
 * Endpoints:
 * - POST /groups/{groupID}/split (authenticated)
 * - POST /groups/{groupID}/anonymous/split (public)
 *
 * Business rules:
 * - Sum of payers.amount MUST equal detail.total
 * - Sum of participants.amount MUST equal detail.total
 */
export interface IRecordGroupExpenseRequest {
  /** Identifier of the creator (user or session) */
  created_by: string;

  /** Session identifier for idempotency and tracking */
  session_id?: string;

  expense_date?: string;

  /** Description of the expense */
  description: string;

  /** List of payers and how much each paid */
  payers: IRecordGroupExpenseParticipant[];

  /** List of participants and how much each owes */
  participants: IRecordGroupExpenseParticipant[];

  /** Detailed breakdown of the expense */
  detail: IRecordGroupExpenseDetail;
}

/**
 * Represents a participant in an expense.
 */
export interface IRecordGroupExpenseParticipant {
  /** Member identifier */
  split_user_id: string;

  /** Amount paid or owed */
  amount: number;
}

/**
 * Detailed breakdown of an expense.
 */
export interface IRecordGroupExpenseDetail {
  /** Base amount before taxes and tip */
  subtotal: number;

  /** Tax amount */
  taxes: number;

  /** Tip amount */
  tip: number;

  /** Total amount (subtotal + taxes + tip) */
  total: number;

  /** Itemized breakdown */
  items: IRecordGroupExpenseItem[];
}

/**
 * Represents an individual item within an expense.
 */
export interface IRecordGroupExpenseItem {
  /** Item description */
  description: string;

  /** Members sharing this item */
  shared_by_ids: string[];

  /** Item subtotal */
  subtotal: number;

  /** Item total (after taxes/tip allocation if applicable) */
  total: number;
}

export interface ISettlementRequest {
  settlements: ISettlementActionRequest[];
}

export interface ISettlementActionRequest {
  group_id: string;
  from: string;
  to: string;
  amount: number;
  settled_by: string;
}

export interface ISettlementResponse {
  status: string; // accepted
}

// =========================
// GET GROUP ACTIVITY TYPES
// =========================

// -------- Expense Detail --------

export interface IGroupExpenseItemDetailItem {
  description: string;
  shared_by: string[];
  subtotal: number;
  tax: number;
  tip: number;
  total: number;
}

export interface IGroupExpenseDetail {
  subtotal: number;
  taxes: number;
  tip: number;
  total: number;
  description: string;
  items: IGroupExpenseItemDetailItem[];
}

// -------- Expense Participants --------

export interface IGroupExpenseParticipant {
  id: string;
  name: string;
  owner: boolean;
  split_amount: number;
  reminder_enabled: boolean;
  payment_confirmed: boolean;
  split_id: string;
  split_user_id: string;
}

// -------- Expense --------

export interface IGroupExpensePayer {
  split_user_id: string;
  amount: number;
}

export interface IGroupExpense {
  id: string;
  created_at: string;
  expense_date: string;
  owner_id: string;
  owner_role: string;
  session_id: string;
  detail: IGroupExpenseDetail;
  participants: IGroupExpenseParticipant[];
  payers: IGroupExpensePayer[];
}

// -------- Settlement --------

export interface IGroupSettlement {
  settlement_id: string;
  group_id: string;
  settled_by: string;
  from: string;
  to: string;
  amount: number;
  created_at: string;
}

// -------- Activity Items (Discriminated Union) --------

interface IBaseGroupActivityItem {
  group_id: string;
  created_at: string;
  expense_date: string;
  created_by: string;
}

export enum GroupActivityItemType {
  EXPENSE = 'expense',
  SETTLEMENT = 'settlement',
}

export interface IGroupActivityItem extends IBaseGroupActivityItem {
  type: GroupActivityItemType;
  expense?: IGroupExpense;
  settlement?: IGroupSettlement;
}

// -------- Response --------

export interface IGetGroupExpensesResponse {
  items: IGroupActivityItem[];
  last_key: string | null;
}
